import { syncExcelMaster } from "@/lib/excelMasterSync";
/**
 * Procesador de facturas RECIBIDAS — vía Excel de ERP → SUENLACE.DAT (.zip).
 * Réplica visual del homónimo de Emitidas.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  FileSpreadsheet,
  Download,
  Loader2,
  Settings2,
  AlertTriangle,
  CheckCircle,
  Building2,
  Upload,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompanyExportSettings } from "@/hooks/useCompanyExportSettings";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import MasterSelector from "@/components/MasterSelector";
import ReceivedMappingProfileDialog from "@/components/ReceivedMappingProfileDialog";
import ThemeToggle from "@/components/ThemeToggle";
import {
  loadCompanyMasters,
  type CompanyMaster,
} from "@/lib/supplierMaster";
import {
  loadAllReceivedMappings,
  type ReceivedInvoiceMapping,
} from "@/lib/receivedInvoiceMapping";
import { importReceivedExcel } from "@/lib/receivedInvoiceImport";
import {
  buildReceivedSuenlaceFile,
  getReceivedVatAccount,
  validateReceivedRows,
  type ReceivedInvoiceRow,
} from "@/lib/a3ecoExportReceived";
import { downloadFilesAsZip } from "@/lib/pdfDownload";
import { formatMoney, formatNumber } from "@/lib/numberFormatting";

const ConvertirExcelRecibidas = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  const [companies, setCompanies] = useState<CompanyMaster[]>([]);
  const [company, setCompany] = useState<CompanyMaster | null>(null);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const orgExportSettings = useCompanyExportSettings(company?.organization_id ?? null);

  const [profiles, setProfiles] = useState<ReceivedInvoiceMapping[]>([]);
  const [profileId, setProfileId] = useState<string>("");
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState<ReceivedInvoiceRow[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user?.id) return;
    loadCompanyMasters().then((list) => setCompanies(list));
  }, [user?.id]);

  const refreshProfiles = useCallback(async () => {
    const list = await loadAllReceivedMappings();
    setProfiles(list);
    setProfileId((prev) => {
      if (list.length === 0) return "";
      if (list.find((p) => p.id === prev)) return prev;
      return list[0].id;
    });
  }, []);

  useEffect(() => {
    refreshProfiles();
    setExcelFile(null);
    setRows([]);
    setNotes([]);
    setWarnings([]);
  }, [company?.id, refreshProfiles]);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === profileId) ?? null,
    [profiles, profileId],
  );

  const handleFileSelected = async (file: File) => {
    setExcelFile(file);
    if (!selectedProfile) {
      toast({
        title: "Selecciona primero una plantilla",
        description: "Necesitas una plantilla de mapeo para interpretar el Excel.",
        variant: "destructive",
      });
      return;
    }
    setImporting(true);
    try {
      const result = await importReceivedExcel(file, selectedProfile);
      if (company?.id && result.rows.length > 0) {
        const cid = company.id;
        void syncExcelMaster("suppliers", cid, result.rows.map((r) => ({
          taxId: r.supplierTaxId, description: r.supplierName, accountCode: r.supplierAccount,
          counterAccount: r.expenseAccount, postalCode: r.postalCode, operationType: r.operationType, 
        }))).catch((err) => console.warn("Alta de maestro desde Excel", err));
      }
      setRows(result.rows);
      setNotes(result.notes);
      setWarnings(validateReceivedRows(result.rows));
      if (result.rows.length === 0) {
        toast({
          title: "No se detectaron facturas",
          description: "Revisa la fila de cabeceras y el mapeo del perfil.",
          variant: "destructive",
        });
      } else {
        toast({
          title: `${result.rows.length} factura(s) importada(s)`,
          description: result.notes.length > 0 ? `${result.notes.length} aviso(s)` : undefined,
        });
      }
    } catch (e) {
      toast({
        title: "Error leyendo Excel",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
      setRows([]);
      setNotes([]);
      setWarnings([]);
    } finally {
      setImporting(false);
    }
  };

  const totals = useMemo(() => {
    let base = 0, vat = 0, total = 0, irpf = 0;
    for (const r of rows) {
      for (const l of r.vatLines) {
        base += Number(l.taxableBase) || 0;
        vat += Number(l.vatAmount) || 0;
      }
      total += Number(r.totalAmount) || 0;
      irpf += Number(r.irpfAmount) || 0;
    }
    return { base, vat, total, irpf };
  }, [rows]);

  const handleGenerate = async () => {
    if (!company?.a3_company_code) {
      toast({
        title: "La empresa no tiene código A3",
        description: "Configura el código A3 de la empresa antes de exportar.",
        variant: "destructive",
      });
      return;
    }
    if (rows.length === 0) return;
    if (warnings.length > 0) {
      toast({
        title: "Corrige los errores antes de exportar",
        description: `${warnings.length} error(es) detectado(s)`,
        variant: "destructive",
      });
      return;
    }
    setGenerating(true);
    try {
      const result = buildReceivedSuenlaceFile(rows, {
        companyCode: company.a3_company_code,
        paymentAccountMap: selectedProfile?.defaults?.paymentAccountMap,
        generatePaymentEntry: selectedProfile?.defaults?.generatePaymentEntry,
        useInvoiceDateAsEntryDate: orgExportSettings.useInvoiceDateAsEntryDate,
        omitVatLedgerNumber: orgExportSettings.omitVatLedgerNumber,
      });
      const blob = new Blob([result.bytes as BlobPart], { type: "application/octet-stream" });
      const lastDot = result.fileName.lastIndexOf(".");
      const zipBase = lastDot > 0 ? result.fileName.slice(0, lastDot) : result.fileName;
      const items: Array<{ file: File | Blob; name: string }> = [
        { file: blob, name: result.fileName },
      ];
      if (excelFile) items.push({ file: excelFile, name: excelFile.name });
      await downloadFilesAsZip(items, `${zipBase}.zip`);
      toast({
        title: "SUENLACE generado",
        description: `${result.invoiceCount} factura(s) · ${result.lineCount} registro(s)`,
      });
    } catch (e) {
      toast({
        title: "Error generando SUENLACE",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(file);
  };

  const canDrop = !!company && !!selectedProfile && !importing;

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-background flex flex-col">
      <header className="border-b bg-primary">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4 flex-wrap">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
            <FileSpreadsheet className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-primary-foreground truncate">
              Procesador de facturas recibidas — desde Excel
            </h1>
            <p className="text-xs text-primary-foreground/60 truncate">
              Convierte el Excel de compras del ERP en un fichero SUENLACE listo para A3
            </p>
          </div>

          <ThemeToggle />
          <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={signOut}>
            <LogOut className="h-3.5 w-3.5" />
            Salir
          </Button>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-6 py-6 space-y-6">
          {!company && (
            <div className="rounded-lg border-2 border-dashed bg-card p-10 text-center">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground/60" />
              <p className="mt-3 text-sm text-muted-foreground">
                Elige una empresa para empezar.
              </p>
              <Button className="mt-4 gap-2" onClick={() => setShowCompanySelector(true)}>
                <Building2 className="h-4 w-4" />
                Elegir empresa
              </Button>
            </div>
          )}

          {company && (
            <section className="rounded-lg border bg-card p-5 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-sm font-semibold">Elige la plantilla</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Selecciona el formato del Excel de compras del ERP del cliente. Las plantillas son comunes para toda la asesoría.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {company.a3_company_code ? (
                    <span className="text-xs text-muted-foreground">
                      Código A3: <strong className="text-foreground">{company.a3_company_code}</strong>
                    </span>
                  ) : (
                    <span className="text-xs text-destructive">⚠ Sin código A3</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setProfileDialogOpen(true)}
                  >
                    <Settings2 className="h-4 w-4" />
                    Gestionar plantillas
                  </Button>
                </div>
              </div>

              {profiles.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center">
                  <p className="text-sm text-muted-foreground">Aún no hay plantillas creadas.</p>
                  <Button size="sm" className="mt-3 gap-2" onClick={() => setProfileDialogOpen(true)}>
                    <Settings2 className="h-4 w-4" />
                    Crear primera plantilla
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {profiles.map((p) => {
                    const isSelected = p.id === profileId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setProfileId(p.id)}
                        className={`text-left rounded-md border p-3 transition-all ${
                          isSelected
                            ? "border-[hsl(var(--brand-green))] bg-[hsl(var(--brand-green))]/10 ring-1 ring-[hsl(var(--brand-green))]"
                            : "border-border hover:border-foreground/30 hover:bg-muted/40"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className={`h-4 w-4 shrink-0 ${isSelected ? "text-[hsl(var(--brand-green))]" : "text-muted-foreground"}`} />
                          <span className="text-sm font-semibold truncate">{p.name}</span>
                        </div>
                        {p.erp_label && (
                          <p className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                            {p.erp_label}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {company && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (canDrop) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={canDrop ? handleDrop : (e) => e.preventDefault()}
              className={`relative rounded-xl border-2 border-dashed transition-all p-12 text-center bg-card ${
                isDragging
                  ? "border-[hsl(var(--brand-green))] bg-[hsl(var(--brand-green))]/5"
                  : "border-border"
              } ${!canDrop ? "opacity-60" : ""}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelected(f);
                  e.target.value = "";
                }}
              />
              <FileSpreadsheet className="mx-auto h-14 w-14 text-muted-foreground/50" />
              <p className="mt-4 text-base font-semibold">
                {!selectedProfile
                  ? "Selecciona una plantilla para empezar"
                  : excelFile
                    ? excelFile.name
                    : "Arrastra aquí el Excel del ERP"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Formatos admitidos: .xlsx, .xls
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={!canDrop}
                className="mt-5 gap-2"
                size="lg"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {excelFile ? "Cambiar Excel" : "Elegir Excel"}
              </Button>
            </div>
          )}

          {rows.length > 0 && (
            <section className="rounded-lg border bg-card overflow-hidden">
              <div className="border-b bg-muted/30 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-sm font-semibold">{rows.length} factura(s) importada(s)</h2>
                <div className="flex items-center gap-2">
                  {warnings.length === 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-green))]">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Listo para exportar
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {warnings.length} error(es)
                    </span>
                  )}
                  <Button
                    className="gap-2 bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(152,40%,45%)]"
                    onClick={handleGenerate}
                    disabled={generating || warnings.length > 0}
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Descargar ZIP SUENLACE
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b">
                <SummaryCell label="Facturas" value={String(rows.length)} />
                <SummaryCell label="Base imponible" value={formatMoney(totals.base)} />
                <SummaryCell label="Cuota IVA" value={formatMoney(totals.vat)} />
                <SummaryCell label="Total" value={formatMoney(totals.total)} />
                {totals.irpf !== 0 && (
                  <SummaryCell label="IRPF retenido" value={formatMoney(totals.irpf)} />
                )}
              </div>

              <div className="overflow-x-auto max-h-[480px]">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium">Fecha</th>
                      <th className="px-3 py-2 font-medium">Nº factura</th>
                      <th className="px-3 py-2 font-medium">Proveedor</th>
                      <th className="px-3 py-2 font-medium">Cta. proveedor</th>
                      <th className="px-3 py-2 font-medium">NIF</th>
                      <th className="px-3 py-2 font-medium">Cta. gasto</th>
                      <th className="px-3 py-2 font-medium">Cta. IVA</th>
                      <th className="px-3 py-2 font-medium text-right">Base</th>
                      <th className="px-3 py-2 font-medium text-right">IVA</th>
                      <th className="px-3 py-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const base = r.vatLines.reduce((a, l) => a + (Number(l.taxableBase) || 0), 0);
                      const vat = r.vatLines.reduce((a, l) => a + (Number(l.vatAmount) || 0), 0);
                      return (
                        <tr key={i} className="border-t hover:bg-muted/20">
                          <td className="px-3 py-1.5 whitespace-nowrap">{r.invoiceDate || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap font-medium">
                            {(r.series ? `${r.series} ` : "") + (r.invoiceNumber || "—")}
                          </td>
                          <td className="px-3 py-1.5">{r.supplierName || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{r.supplierAccount || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{r.supplierTaxId || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{r.expenseAccount || "—"}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{getReceivedVatAccount(r) || "—"}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(base)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(vat)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                            {formatMoney(Number(r.totalAmount) || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {warnings.length > 0 && (
                <div className="border-t border-destructive/30 bg-destructive/5 p-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {warnings.length} error(es) a corregir antes de exportar
                  </div>
                  <ul className="text-xs text-destructive/90 list-disc pl-5 space-y-0.5 max-h-40 overflow-y-auto">
                    {warnings.slice(0, 50).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                    {warnings.length > 50 && (
                      <li className="italic">… y {warnings.length - 50} más</li>
                    )}
                  </ul>
                </div>
              )}

              {notes.length > 0 && (
                <div className="border-t border-amber-400/30 bg-amber-50/40 dark:bg-amber-950/20 p-3 space-y-1">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                    Avisos durante la importación
                  </p>
                  <ul className="text-xs text-amber-700/90 dark:text-amber-300/90 list-disc pl-5 space-y-0.5 max-h-32 overflow-y-auto">
                    {notes.slice(0, 30).map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          <p className="text-[10px] text-muted-foreground text-center">
            Módulo nuevo. Revisa siempre el primer SUENLACE generado de cada ERP en A3 antes de
            traspasar en producción.
          </p>
        </div>
      </main>

      <MasterSelector
        open={showCompanySelector}
        onSelect={(c) => {
          setShowCompanySelector(false);
          setCompany(c);
        }}
        onCancel={() => setShowCompanySelector(false)}
      />

      <ReceivedMappingProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onChange={refreshProfiles}
      />
    </div>
  );
};

const SummaryCell = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border bg-background px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-sm font-semibold mt-0.5">{value}</p>
  </div>
);

export default ConvertirExcelRecibidas;
